import { trackEvent } from './analytics';
import { analyticsAllowed } from './guestAnalytics';
import { isTranscriptEmpty } from './transcriptUtils';
import { reviewMeasurements, reviewFailureCode } from './reviewMetrics';
import { resultQualityMeasurements } from './resultQuality';

const memory = new Map();
const TTL = 7 * 86400000;
const keyFor = record => `${record.id}:${record.is_preview ? 'preview' : 'full'}`;
const prefix = 'instanttranscriber_job_observations_v1:';
function read(userId) {
  let data = memory.get(userId) || { pending: {}, seen: {} };
  try { data = JSON.parse(localStorage.getItem(prefix + userId) || 'null') || data; } catch {}
  if (typeof data !== 'object' || Array.isArray(data)) data = { pending: {}, seen: {} };
  for (const field of ['pending', 'seen']) {
    data[field] = Object.fromEntries(Object.entries(data[field] || {})
      .filter(([, time]) => Date.now() - Number(time) < TTL).slice(-500));
  }
  return data;
}
function write(userId, data) {
  if (!memory.has(userId) && memory.size >= 20) memory.delete(memory.keys().next().value);
  memory.set(userId, data);
  try { localStorage.setItem(prefix + userId, JSON.stringify(data)); } catch {}
}

export function observeJobOutcome(record, userId, source) {
  if (!userId || !record?.id || !analyticsAllowed()) return false;
  const status = String(record.status || '').trim().toLowerCase();
  const completed = status.startsWith('completed');
  if (!completed && !status.startsWith('error')) return false;
  const data = read(userId);
  const key = keyFor(record);
  const outcomeKey = `${key}:${completed ? 'completed' : 'failed'}`;
  if (data.seen[outcomeKey]) return false;
  const sent = trackEvent(completed ? 'transcription_completed' : 'transcription_failed', {
    source, status: completed ? 'completed' : 'failed',
    job_source: ['youtube', 'api'].includes(record.source) ? record.source : 'web',
    result_type: record.is_preview ? 'preview' : 'full',
    has_language: Boolean(record.language), has_duration: Boolean(record.duration),
    ...(completed ? {} : { error_code: status.includes('modal job not found') ? 'modal_job_missing' : 'worker_failed' }),
  });
  if (sent) {
    data.seen[outcomeKey] = Date.now();
    delete data.pending[key];
    write(userId, data);
    if (completed) observeNewResultOutcome(record, userId, source);
  }
  return sent;
}

export function observeHistoryOutcomes(records, userId) {
  if (!userId || !analyticsAllowed()) return;
  let data = read(userId);
  for (const record of records.slice(0, 500)) {
    const key = keyFor(record);
    const status = String(record.status || '').trim().toLowerCase();
    if (status.startsWith('completed') || status.startsWith('error')) {
      // Old historical transcripts are not new completions. Pending state
      // survives a reload/tab close, and status/history races share this ledger.
      if (data.pending[key]) {
        observeJobOutcome(record, userId, 'history_refresh');
        data = read(userId);
      }
      observeNewResultOutcome(record, userId, 'history_refresh');
      data = read(userId);
    } else if (['uploaded', 'queued', 'starting', 'transcribing', 'processing', 'running'].includes(status)) {
      data.pending[key] = Date.now();
      write(userId, data);
    }
  }
}

export function observePendingJob(record, userId) {
  if (!userId || !record?.id || !analyticsAllowed()) return;
  const data = read(userId);
  data.pending[keyFor(record)] = Date.now();
  write(userId, data);
}

export function observeNewResultOutcome(record, userId, source) {
  if (!userId || !record?.id || !analyticsAllowed()) return false;
  const data = read(userId);
  const key = keyFor(record);
  // Prefetching or revisiting old history cannot invent a new completion.
  if (!data.pending[key] && !data.seen[`${key}:completed`]) return false;
  return observeResultOutcome(record, userId, source);
}

export function observeResultOutcome(record, userId, source) {
  if (!userId || !record?.id || !analyticsAllowed() || !/^(completed|succeeded)/i.test(record.status || '')) return false;
  const data = read(userId);
  const hasContent = ['text','empty'].includes(record.result_state)
    || typeof record.transcription_text === 'string' || Array.isArray(record.light_segments)
    || Array.isArray(record.segments) || Array.isArray(record.full?.segments);
  const state = hasContent ? (isTranscriptEmpty(record) ? 'empty' : 'text') : null;
  const key = `${keyFor(record)}:content:${state}`;
  let sent = false;
  if (state && !data.seen[key]) {
    sent = trackEvent(state === 'empty' ? 'transcription_empty_result' : 'transcription_result_ready', {
      source, result_state: state, result_type: record.is_preview ? 'preview' : 'full',
      job_source: ['youtube','api'].includes(record.source) ? record.source : 'web',
      ...(state === 'empty' ? resultQualityMeasurements(record) : {}),
    });
    if (sent) data.seen[key] = Date.now();
  }
  const review = record.speaker_postprocessing || record.full?.diarization?.high_quality_speaker_annotation;
  if (['applied','llm_failed'].includes(review?.status)) {
    const reviewKey = `${keyFor(record)}:review:${review.status}`;
    if (!data.seen[reviewKey]) {
      const emitted = trackEvent('transcript_enhancement_outcome', {
        source, status: review.status === 'applied' ? 'completed' : 'failed',
        ...reviewMeasurements(review),
        operation: Number(review.deferred_retry_count) > 0 ? 'speaker_review_delayed_retry' : 'speaker_review',
        retry_count: Math.max(0, Math.min(20, Number(review.retry_count) || 0)),
        ...(review.status === 'llm_failed' ? { error_code: reviewFailureCode(review.error_code) } : {}),
      });
      if (emitted) { data.seen[reviewKey] = Date.now(); sent = true; }
    }
  }
  if (sent) write(userId, data);
  return sent;
}
